/**
 * Atom hydration utilities for server-side rendering
 *
 * Provides helper functions to dehydrate atoms on the server and
 * hydrate them on the client using Effect Schema for type-safe serialization.
 *
 * @since 1.0.0
 */
import type { Hydration } from "@effect-atom/atom-react";
import { Effect, Schema } from "effect";
import type { HydratedEnvelope } from "./atoms/serializable.ts";

type DehydratedAtom = Hydration.DehydratedAtom;

/**
 * Atom definition for hydration
 *
 * Pairs an Effect that fetches data with a Schema for serialization.
 *
 * IMPORTANT: The effect must be fully self-contained with all dependencies
 * provided via Effect.provide() before passing to hydrateAtoms.
 *
 * @since 1.0.0
 */
export interface AtomDefinition<A, I, R, E> {
  /** Effect Schema for encoding/decoding the atom value */
  schema: Schema.Schema<A, I, R>;
  /** Effect that fetches the atom's data - must have no dependencies (Deps = never) */
  effect: Effect.Effect<A, E, never>;
}

/**
 * Context passed to beforeLoad hooks
 *
 * Contains dehydrated atoms from parent routes.
 * Uses index signature to allow additional context properties (e.g., hostname).
 *
 * @since 1.0.0
 */
export interface BeforeLoadContext {
  context?: {
    dehydratedAtoms?: Array<DehydratedAtom>;
    [key: string]: unknown;
  } | undefined;
}

/**
 * Hydrate atoms for server-side rendering (curried version)
 *
 * This helper function:
 * 1. Runs all provided Effects in parallel to fetch data
 * 2. Encodes values using their schemas
 * 3. Creates dehydrated atom objects
 * 4. Merges with parent route's dehydrated atoms
 *
 * Use this in route `beforeLoad()` hooks to prepare atom state for SSR.
 * The curried API allows for cleaner composition with TanStack Start routes.
 *
 * @example
 * ```typescript
 * // In route beforeLoad:
 * beforeLoad: hydrateAtoms({
 *   currentUser: {
 *     schema: Schema.Option(Person.json),
 *     effect: Effect.gen(function*() {
 *       const user = yield* fetchUser()
 *       return Option.some(user)
 *     })
 *   }
 * })
 * ```
 *
 * @param atoms - Object mapping atom keys to their definitions (schema + effect)
 * @returns Function that takes context and returns Promise of dehydrated atoms
 * @since 1.0.0
 */

/**
 * A TanStack route match, as far as hydration cares: its resolved `context` may
 * carry the `dehydratedAtoms` its `beforeLoad` accumulated.
 *
 * @since 1.0.0
 */
export interface MatchWithDehydration {
  readonly context?: { readonly dehydratedAtoms?: Array<DehydratedAtom> } | undefined;
}

/**
 * Pick the dehydrated atoms the root `HydrationBoundary` must seed the registry
 * with at first (SSR) paint.
 *
 * Every `/orca/*` route's `beforeLoad` merges its parent's `dehydratedAtoms`
 * with the seed it fetched (see `mergeAtoms` in `orca-ssr.ts`), so route context
 * accumulates DOWN the match tree: the ROOT match carries only the atoms the
 * root seeded (the current user), while each child route's SSR seed — the orca
 * task list, project list, project detail, runner registry — lives in a
 * DESCENDANT match. The deepest match therefore holds the full accumulated set.
 *
 * Reading the root match alone (as `RootComponent` did before OL-20) dropped
 * every child seed: the child atom's own RPC effect then ran during the server
 * render, failed synchronously against the browser-oriented RPC client, and the
 * route server-rendered its error frame ("… could not be loaded", "No orca
 * project with this id.") even though the same response carried the correct
 * `Success` seed — a hydration text mismatch (minified React error #418) and a
 * flash of the error state (or a blank page) on every cold load.
 *
 * Walking from the leaf up returns the richest context available and is a no-op
 * for a plain single-match render.
 *
 * @since 1.0.0
 */
export const dehydratedAtomsFromMatches = (
  matches: ReadonlyArray<MatchWithDehydration>,
): Array<DehydratedAtom> => {
  for (let index = matches.length - 1; index >= 0; index -= 1) {
    const atoms = matches[index]?.context?.dehydratedAtoms;
    if (atoms !== undefined) return atoms;
  }
  return [];
};

export const hydrateAtoms = <
  T extends Record<string, AtomDefinition<any, any, any, any>>,
>(
  atoms: T,
) =>
// oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start server functions require async/await
async (context: BeforeLoadContext): Promise<{ dehydratedAtoms: Array<DehydratedAtom> }> => {
  // 1. Get parent atoms from context
  const parentAtoms = context.context?.dehydratedAtoms ?? [];

  // 2. Run all Effects in parallel
  const atomEntries = Object.entries(atoms);

  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- Effect.runPromise requires await
  const results = await Effect.runPromise(
    Effect.all(
      atomEntries.map(([key, { effect, schema }]) =>
        Effect.map(effect, (value) => ({
          key,
          value,
          schema,
        }))
      ),
      { concurrency: "unbounded" },
    ),
  );

  // 3. Encode values with schema and wrap in the Success envelope expected by
  //    `hydratable` (the atom's serializable decode reads `{ _tag, value }`).
  const encodedResults: Array<{ key: string; value: HydratedEnvelope }> = [];
  for (const { key, schema, value } of results) {
    const encoded = Schema.encodeSync(schema as Schema.Schema<any, any>)(value);
    encodedResults.push({ key, value: { _tag: "Success", value: encoded } });
  }

  // 4. Create dehydrated atoms manually with the required brand
  const newAtoms: Array<DehydratedAtom> = encodedResults.map(({ key, value }) => ({
    "~@effect-atom/atom/DehydratedAtom": true as const,
    key,
    value,
    dehydratedAt: Date.now(),
  }));

  // 5. Merge with parent atoms and return
  return {
    dehydratedAtoms: [...parentAtoms, ...newAtoms],
  };
};
